import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  Search,
  FileText,
  Construction
} from 'lucide-react';
import { Button } from '@/Components/ui/button';

// Ciclo do hero: o título fica 10s, depois 4 cards de impacto entram um a um
// (5s cada, virando/deslizando da direita), e volta ao título. Cada card =
// uma palavra de impacto + uma frase curta, ambas apoiadas em requisitos
// reais do sistema (docs/REQUISITOS.md RF01/RF02/RF03/RF04/RF07), não
// inventadas.
const HERO_TITLE_MS = 10000;
const HERO_CARD_MS = 5000;
const HERO_IMPACT_CARDS = [
  { word: 'Transparência', detail: 'Toda ação registrada e auditável — quem fez o quê, quando.' },
  { word: 'Controle', detail: 'Status e prazos das parcerias calculados automaticamente.' },
  { word: 'Agilidade', detail: 'Importação em lote e relatórios em PDF, gerados na hora.' },
  { word: 'Segurança', detail: 'Acesso hierárquico e sessão protegida em cada login.' },
];
const HERO_PHASES = [{ type: 'title' }, ...HERO_IMPACT_CARDS.map((c) => ({ type: 'card', ...c }))];

// Título com efeito de máquina de escrever — digita as duas linhas em sequência
// e some o cursor ao terminar. Definido fora do componente da página: como o
// título entra/sai de novo a cada volta do ciclo do hero, cada montagem já
// reinicia a digitação sozinha (não precisa de key manual nem de timer externo).
const TYPE_LINE_1 = 'Sistema de Controle';
const TYPE_LINE_2 = 'Parceria & OSC';
const TYPE_SPEED_MS = 110;
const TYPE_LINE_BREAK_PAUSE_MS = 550;
function HeroTitleTypewriter({ prefersReducedMotion }) {
  const fullLength = TYPE_LINE_1.length + TYPE_LINE_2.length;
  const [typedCount, setTypedCount] = useState(prefersReducedMotion ? fullLength : 0);

  useEffect(() => {
    if (prefersReducedMotion || typedCount >= fullLength) return undefined;
    // Pausa maior bem na virada da linha 1 pra 2, pra dar a sensação clara de
    // "uma linha terminou, a próxima está começando" em vez de tudo borrado junto.
    const atLineBreak = typedCount === TYPE_LINE_1.length;
    const delay = atLineBreak ? TYPE_LINE_BREAK_PAUSE_MS : TYPE_SPEED_MS;
    const t = setTimeout(() => setTypedCount((c) => c + 1), delay);
    return () => clearTimeout(t);
  }, [typedCount, fullLength, prefersReducedMotion]);

  const line1 = TYPE_LINE_1.slice(0, Math.min(typedCount, TYPE_LINE_1.length));
  const line2 = TYPE_LINE_2.slice(0, Math.max(0, typedCount - TYPE_LINE_1.length));
  const showCaret = typedCount < fullLength;
  // O cursor precisa ficar na linha que está ativa de fato — enquanto a linha 1
  // não termina (typedCount < tamanho dela) ele fica nela; depois disso (incluindo
  // a pausa da virada) ele passa pra linha 2, nunca "flutuando" longe do texto.
  const caretOnLine1 = typedCount < TYPE_LINE_1.length;
  const caret = <span className="inline-block w-[2px] sm:w-[3px] h-[0.85em] align-middle bg-[#0074f6] ml-1 animate-pulse" />;

  return (
    <>
      <span className="bg-gradient-to-r from-[#003098] to-[#0074f6] bg-clip-text text-transparent">
        {line1}
        {showCaret && caretOnLine1 && caret}
      </span>
      <br />
      <span className="text-[#003098]">
        {line2}
        {showCaret && !caretOnLine1 && caret}
      </span>
    </>
  );
}

// Gerador pseudoaleatório determinístico: sempre devolve um inteiro de 1 a 10 a
// partir de uma semente (seno + fração) — não é Math.random(), então o desenho da
// onda é sempre o mesmo cálculo, mas cada crista tem uma altura diferente das outras.
const alternate1to10 = (seed) => {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return 1 + Math.floor((x - Math.floor(x)) * 10);
};

// Onda de mar de verdade: crista suave (Catmull-Rom, sem "dentes" retos) que se
// repete perfeitamente a cada `width` px, por isso pode rolar horizontalmente sem
// costura visível (técnica padrão de wave-divider: desenha 2 cópias lado a lado e
// desliza -50%, quando a 2ª cópia chega no lugar da 1ª ninguém percebe o corte).
const buildLoopingWavePath = (crests, seedBase, baseline, amplitude, width, height) => {
  const yVals = Array.from({ length: crests }, (_, i) => {
    const v = alternate1to10(seedBase + i * 3.7); // sempre 1 a 10
    return baseline + ((v - 5.5) / 4.5) * amplitude;
  });
  const step = width / crests;
  const pts = Array.from({ length: 2 * crests + 1 }, (_, i) => ({ x: i * step, y: yVals[i % crests] }));
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  d += ` L${2 * width},${height} L0,${height} Z`;
  return d;
};

// Três camadas em paralaxe (mar ao fundo, mais claro e lento → onda de frente, mais
// escura e rápida), cada uma rolando no seu próprio ritmo — é isso que dá profundidade
// de mar real. Amplitudes maiores + bases diferentes fazem as cristas de uma camada
// invadirem o vale da outra (a de trás aparece por cima da de frente em certos pontos —
// as "pontas" se cruzam de verdade, não é só cor sobreposta). Regra fixa: a base do path
// sempre fecha em `height` (fundo do viewBox), então a onda NUNCA descola da base do
// rodapé — só o contorno (x) rola, a âncora inferior nunca se mexe.
const OCEAN_W = 1440;
const OCEAN_H = 140;
const OCEAN_LAYERS = [
  { crests: 5, seed: 11, baseline: 92, amplitude: 11, opacity: 0.18, scrollDuration: 26, color: '#bfdbfe' },
  { crests: 4, seed: 47, baseline: 76, amplitude: 17, opacity: 0.28, scrollDuration: 18, color: '#60a5fa' },
  { crests: 3, seed: 83, baseline: 64, amplitude: 24, opacity: 0.4, scrollDuration: 12, color: '#0074f6' },
].map((layer) => ({
  ...layer,
  path: buildLoopingWavePath(layer.crests, layer.seed, layer.baseline, layer.amplitude, OCEAN_W, OCEAN_H),
}));

// Efeito "onda tocando o texto": a crista mais alta da onda de frente agora chega
// perto o bastante da última linha do rodapé pra realmente encostar de vez em quando —
// por isso o texto tinge de azul-mar no mesmo ritmo em que UMA crista passa por baixo
// dele (período total / nº de cristas da onda de frente), não no ciclo inteiro da onda.
// Atraso diferente por bloco (esquerda/centro/direita) simula a varredura da esquerda
// pra direita "molhando" cada texto na sua vez.
const WAVE_TOUCH_COLOR = '#0074f6';
const FRONT_WAVE = OCEAN_LAYERS[2];
const CREST_PASS_MS = FRONT_WAVE.scrollDuration / FRONT_WAVE.crests;
const waveTouchAnimation = (baseColor, delay) => ({
  animate: { color: [baseColor, WAVE_TOUCH_COLOR, baseColor] },
  transition: { duration: CREST_PASS_MS, repeat: Infinity, ease: 'easeInOut', delay },
});

export default function LandingPage() {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const [scrollY, setScrollY] = useState(0);
  const [parallaxValues, setParallaxValues] = useState({
    parallax1: 0,
    parallax2: 0,
    parallax3: 0,
    heroOpacity: 1
  });

  useEffect(() => {
    // Função para atualizar parallax com requestAnimationFrame para suavidade
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          setScrollY(currentScrollY);
          
          // Atualizar valores de paralaxe (funciona em ambas direções)
          setParallaxValues({
            parallax1: currentScrollY * 0.3,
            parallax2: currentScrollY * 0.5,
            parallax3: currentScrollY * 0.2,
            heroOpacity: Math.max(0, 1 - currentScrollY / 400)
          });
          
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ciclo automático do hero (título ⇄ 4 cards de impacto) — parado se o
  // usuário pediu movimento reduzido, mostrando só o título fixo.
  const [heroPhaseIndex, setHeroPhaseIndex] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion) return;
    const duration = HERO_PHASES[heroPhaseIndex].type === 'title' ? HERO_TITLE_MS : HERO_CARD_MS;
    const t = setTimeout(() => {
      setHeroPhaseIndex((i) => (i + 1) % HERO_PHASES.length);
    }, duration);
    return () => clearTimeout(t);
  }, [heroPhaseIndex, prefersReducedMotion]);
  const heroPhase = prefersReducedMotion ? HERO_PHASES[0] : HERO_PHASES[heroPhaseIndex];

  const oscPreviewCards = [
    { name: 'Nome da OSC', cnpj: '••.•••.•••/••••-••', status: 'Situação' },
    { name: 'Nome da OSC', cnpj: '••.•••.•••/••••-••', status: 'Situação' },
    { name: 'Nome da OSC', cnpj: '••.•••.•••/••••-••', status: 'Situação' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-slate-50 text-slate-900 overflow-hidden">
      {/* Header/Navbar */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrollY > 50 ? 'bg-white/95 backdrop-blur-lg shadow-xl border-b border-[#003098]/10' : 'bg-white/80 backdrop-blur-sm'
        }`}
      >
        <div className="container mx-auto px-3 sm:px-6 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            <motion.a
              href="https://corumba.ms.gov.br/"
              rel="noopener noreferrer"
              title="Ir para o portal da Prefeitura Municipal de Corumbá/MS"
              className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1"
              whileHover={{ scale: 1.02 }}
            >
              <img
                src="/brazao_logo.png"
                alt="Prefeitura de Corumbá"
                className="h-10 sm:h-12 md:h-14 w-auto flex-shrink-0"
              />
              <div className="border-l-2 border-[#003098] pl-2 sm:pl-4 min-w-0">
                <h1 className="text-xs sm:text-sm md:text-lg font-bold text-[#003098] truncate">
                  Controle e Monitoramento
                </h1>
                <p className="text-[10px] sm:text-xs text-[#0074f6] truncate">Parcerias & OSC</p>
              </div>
            </motion.a>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Botão Consulta Pública de Emendas */}
              <div className="relative group hidden md:block">
                <Button
                  onClick={() => navigate('/emendas')}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all text-xs sm:text-sm h-8 sm:h-9 md:h-10 px-3 sm:px-4 whitespace-nowrap"
                >
                  <Search className="mr-1 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden lg:inline">Consulta Pública de Emendas</span>
                  <span className="lg:hidden">Emendas</span>
                </Button>
                {/* Tooltip */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-4 py-3 bg-emerald-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-50 shadow-2xl w-64">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
                    <div className="text-left">
                      <p className="font-bold text-emerald-300 mb-1">� Consulta Pública</p>
                      <p className="text-slate-200 leading-relaxed">
                        Acesse dados completos de emendas impositivas parlamentares com filtros e busca avançada
                      </p>
                    </div>
                  </div>
                  {/* Seta para cima */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-[-1px] border-[6px] border-transparent border-b-emerald-900"></div>
                </div>
              </div>
              
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-[#003098] to-[#0074f6] hover:from-[#002070] hover:to-[#0060d0] text-white shadow-lg shadow-[#003098]/30 text-xs sm:text-sm h-8 sm:h-9 md:h-10 px-3 sm:px-4 whitespace-nowrap"
              >
                Entrar
                <ArrowRight className="ml-1 sm:ml-2 w-3 h-3 sm:w-4 sm:h-4" />
              </Button>

              <Button
                onClick={() => navigate('/sobre')}
                variant="outline"
                className="hidden md:inline-flex border-2 border-[#003098] text-[#003098] hover:bg-[#003098] hover:text-white text-xs sm:text-sm h-8 sm:h-9 md:h-10 px-3 sm:px-4 whitespace-nowrap font-semibold"
              >
                Sobre
              </Button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section com Paralaxe */}
      <section className="relative flex items-center justify-center overflow-hidden pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 sm:px-6">
        {/* Background com efeito de grade institucional */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#003098_1px,transparent_1px),linear-gradient(to_bottom,#003098_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-[0.03]" />
        
        {/* Elementos flutuantes com paralaxe - cores institucionais */}
        <div 
          style={{ 
            transform: `translateY(${parallaxValues.parallax1}px)`,
            opacity: Math.max(0.3, 1 - scrollY / 800),
            transition: 'transform 0.1s ease-out, opacity 0.1s ease-out'
          }}
          className="absolute top-20 left-10 w-96 h-96 bg-[#003098]/10 rounded-full blur-3xl will-change-transform"
        />
        <div 
          style={{ 
            transform: `translateY(${parallaxValues.parallax2}px)`,
            opacity: Math.max(0.3, 1 - scrollY / 1000),
            transition: 'transform 0.1s ease-out, opacity 0.1s ease-out'
          }}
          className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-[#0074f6]/10 rounded-full blur-3xl will-change-transform"
        />
        <div 
          style={{ 
            transform: `translateY(${parallaxValues.parallax3}px)`,
            opacity: Math.max(0.3, 1 - scrollY / 900),
            transition: 'transform 0.1s ease-out, opacity 0.1s ease-out'
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#efb110]/5 rounded-full blur-3xl will-change-transform"
        />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ opacity: parallaxValues.heroOpacity }}
            className="text-center max-w-5xl mx-auto"
          >
            <div className="relative mb-8 sm:mb-10 min-h-[150px] sm:min-h-[160px] md:min-h-[170px] flex items-center justify-center [perspective:1400px]">
              <AnimatePresence mode="wait">
                {heroPhase.type === 'title' ? (
                  <motion.h1
                    key="hero-title"
                    initial={{ opacity: 0, rotateY: -70, x: 60 }}
                    animate={{ opacity: 1, rotateY: 0, x: 0 }}
                    exit={{ opacity: 0, rotateY: 70, x: -60 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.3] px-2 py-1"
                  >
                    <HeroTitleTypewriter prefersReducedMotion={prefersReducedMotion} />
                  </motion.h1>
                ) : (
                  <motion.div
                    key={`hero-card-${heroPhase.word}`}
                    initial={{ opacity: 0, rotateY: -70, x: 80 }}
                    animate={{ opacity: 1, rotateY: 0, x: 0 }}
                    exit={{ opacity: 0, rotateY: 70, x: -80 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute bg-white border-2 border-[#003098]/15 rounded-2xl shadow-xl px-6 sm:px-10 py-5 sm:py-6 max-w-lg mx-auto"
                  >
                    <p className="text-2xl sm:text-3xl md:text-4xl font-bold leading-[1.3] py-0.5 bg-gradient-to-r from-[#003098] to-[#0074f6] bg-clip-text text-transparent mb-1">
                      {heroPhase.word}
                    </p>
                    <p className="text-xs sm:text-sm md:text-base text-slate-600">{heroPhase.detail}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <p className="text-xs sm:text-sm text-slate-500 mb-6 sm:mb-8 px-2">
              Prefeitura Municipal de Corumbá/MS
            </p>

            <div className="flex items-center justify-center px-4">
              <Button
                size="lg"
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-[#003098] to-[#0074f6] hover:from-[#002070] hover:to-[#0060d0] text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 text-sm sm:text-base md:text-lg shadow-2xl shadow-[#003098]/30 hover:shadow-[#003098]/50 transition-all font-semibold w-full sm:w-auto"
              >
                Acessar Sistema
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Consulta Pública de OSCs - painel de destaque, funcionalidade futura */}
      <section id="consulta-oscs" className="py-16 sm:py-20 md:py-24 relative bg-white/50">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <div className="relative bg-white border-2 border-[#003098]/15 rounded-3xl p-6 sm:p-10 md:p-12 shadow-xl overflow-hidden">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#003098_1px,transparent_1px),linear-gradient(to_bottom,#003098_1px,transparent_1px)] bg-[size:3rem_3rem] opacity-[0.03]" />

              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#003098]/10 text-[#003098] rounded-full text-xs sm:text-sm font-bold mb-4">
                    <Construction className="w-4 h-4" />
                    Em desenvolvimento
                  </div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-[#003098]">
                    Consulta Pública de OSCs
                  </h2>
                  <p className="text-sm sm:text-base md:text-lg text-slate-700 leading-relaxed">
                    Em breve, qualquer cidadão poderá consultar aqui, sem login, todas as OSCs com parcerias ativas — nome, CNPJ e situação atual.
                  </p>
                </div>

                <div className="space-y-3">
                  {oscPreviewCards.map((card, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4 opacity-70"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-slate-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-400 truncate">{card.name}</p>
                          <p className="text-xs text-slate-400">{card.cnpj}</p>
                        </div>
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 bg-slate-200 px-2 py-1 rounded-full whitespace-nowrap">
                        {card.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative pt-8 pb-11 sm:pb-14 bg-white border-t-2 border-[#003098] overflow-hidden">
        {/* Mar decorativo — 3 ondas reais (crista suave, sem repetição visível) rolando.
            Regra fixa: cada path fecha em bottom-0/height, então a onda nunca descola
            da base do rodapé — só o contorno rola, a âncora inferior não se mexe. */}
        <div className="absolute inset-x-0 bottom-0 h-14 sm:h-20 overflow-hidden pointer-events-none" aria-hidden="true">
          {OCEAN_LAYERS.map((wave, i) => (
            <motion.div
              key={i}
              className="absolute bottom-0 left-0 h-full"
              style={{ width: '200%', opacity: wave.opacity }}
              animate={prefersReducedMotion ? undefined : { x: ['0%', '-50%'] }}
              transition={{ duration: wave.scrollDuration, ease: 'linear', repeat: Infinity }}
            >
              <svg viewBox={`0 0 ${OCEAN_W * 2} ${OCEAN_H}`} preserveAspectRatio="none" className="w-full h-full">
                <path d={wave.path} fill={wave.color} />
              </svg>
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="flex flex-col items-center sm:flex-row sm:justify-between gap-y-3 gap-x-6">
            {/* No mobile cada bloco vira uma "caixa" opaca — protege a leitura mesmo
                se a onda de frente encostar perto do texto. No desktop a caixa some
                (há folga de sobra e o efeito de cor já resolve). */}
            <a
              href="https://corumba.ms.gov.br/"
              rel="noopener noreferrer"
              title="Ir para o portal da Prefeitura Municipal de Corumbá/MS"
              className="flex items-center gap-2 bg-white/95 rounded-xl px-4 py-2 shadow-sm ring-1 ring-black/5 sm:bg-transparent sm:shadow-none sm:ring-0 sm:rounded-none sm:px-0 sm:py-0"
            >
              <img src="/brazao_logo.png" alt="Prefeitura de Corumbá" className="h-8 w-auto" />
              <motion.span
                className="font-bold text-xs whitespace-nowrap"
                style={{ color: '#003098' }}
                animate={prefersReducedMotion ? undefined : waveTouchAnimation('#003098', 0).animate}
                transition={waveTouchAnimation('#003098', 0).transition}
              >
                Prefeitura Municipal de Corumbá/MS
              </motion.span>
            </a>

            <div className="flex flex-col items-center gap-0.5 text-center bg-white/95 rounded-xl px-4 py-2 shadow-sm ring-1 ring-black/5 sm:bg-transparent sm:shadow-none sm:ring-0 sm:rounded-none sm:px-0 sm:py-0">
              <motion.span
                className="text-xs font-medium whitespace-nowrap"
                style={{ color: '#475569' }}
                animate={prefersReducedMotion ? undefined : waveTouchAnimation('#475569', CREST_PASS_MS / 3).animate}
                transition={waveTouchAnimation('#475569', CREST_PASS_MS / 3).transition}
              >
                © {new Date().getFullYear()} Todos os direitos reservados
              </motion.span>
              <motion.span
                className="text-[11px] font-medium whitespace-nowrap"
                style={{ color: '#94a3b8' }}
                animate={prefersReducedMotion ? undefined : waveTouchAnimation('#94a3b8', CREST_PASS_MS / 3).animate}
                transition={waveTouchAnimation('#94a3b8', CREST_PASS_MS / 3).transition}
              >
                Sistema de Parcerias v5.0
              </motion.span>
            </div>

            <motion.span
              className="text-xs whitespace-nowrap text-center sm:text-right bg-white/95 rounded-xl px-4 py-2 shadow-sm ring-1 ring-black/5 sm:bg-transparent sm:shadow-none sm:ring-0 sm:rounded-none sm:px-0 sm:py-0"
              style={{ color: '#64748b' }}
              animate={prefersReducedMotion ? undefined : waveTouchAnimation('#64748b', (CREST_PASS_MS * 2) / 3).animate}
              transition={waveTouchAnimation('#64748b', (CREST_PASS_MS * 2) / 3).transition}
            >
              Desenvolvido pelo Núcleo de Gestão Estratégica/SEPRAD
            </motion.span>
          </div>
        </div>
      </footer>
    </div>
  );
}
